import { EditorView } from '@tiptap/pm/view';

interface IComputeCaretScrollDeltaParams {
  caretTop: number;
  caretBottom: number;
  visibleTop: number;
  visibleBottom: number;
  buffer?: number;
}

export function computeCaretScrollDelta({
  caretTop,
  caretBottom,
  visibleTop,
  visibleBottom,
  buffer = 24,
}: IComputeCaretScrollDeltaParams): number {
  if (caretBottom > visibleBottom - buffer) {
    return caretBottom - (visibleBottom - buffer);
  }

  if (caretTop < visibleTop + buffer) {
    return caretTop - (visibleTop + buffer);
  }

  return 0;
}

export function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  let current = element?.parentElement ?? null;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const isScrollableOverflow = overflowY === 'auto' || overflowY === 'scroll';

    if (isScrollableOverflow && current.scrollHeight > current.clientHeight) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

// MobileActionPanel is a fixed-position sibling that overlays the bottom ~60-100px
// of the scroll container (its own height + bottom offset + safe-area-inset-bottom),
// without shrinking the container's own layout box. The default 24px buffer isn't
// enough to clear it, so the caret can end up visible-by-the-numbers but physically
// hidden under the panel. Keep this in sync with MobileActionPanel.module.css.
const BOTTOM_OVERLAY_CLEARANCE = 96;

export function scrollCaretIntoView(view: EditorView): void {
  const scrollContainer = findScrollableAncestor(view.dom as HTMLElement);

  if (!scrollContainer) {
    return;
  }

  let caretRect: ReturnType<EditorView['coordsAtPos']>;

  try {
    caretRect = view.coordsAtPos(view.state.selection.head);
  } catch {
    // coordsAtPos can throw on transient layout states (e.g. a nodeView not yet rendered).
    // Skipping the scroll adjustment is safe — it just retries on the next selection update.
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const { visualViewport } = window;
  const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
  const viewportBottom = viewportOffsetTop + (visualViewport?.height ?? window.innerHeight);

  const visibleTop = Math.max(containerRect.top, viewportOffsetTop);
  const visibleBottom = Math.min(containerRect.bottom, viewportBottom);

  const delta = computeCaretScrollDelta({
    caretTop: caretRect.top,
    caretBottom: caretRect.bottom,
    visibleTop,
    visibleBottom,
    buffer: BOTTOM_OVERLAY_CLEARANCE,
  });

  if (delta !== 0) {
    scrollContainer.scrollBy({ top: delta, behavior: 'auto' });
  }
}
